import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LangProvider } from "@/i18n/LangContext";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Landing from "@/pages/Landing";
import { Login, Register } from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import AppShell from "@/pages/AppShell";
import Dashboard from "@/pages/Dashboard";
import Chat from "@/pages/Chat";
import Diary from "@/pages/Diary";
import Ingest from "@/pages/Ingest";
import Proposals from "@/pages/Proposals";
import Calendar from "@/pages/Calendar";
import Shopping from "@/pages/Shopping";
import Recipes from "@/pages/Recipes";
import Family from "@/pages/Family";
import Agreements from "@/pages/Agreements";
import Memory from "@/pages/Memory";
import Medications from "@/pages/Medications";
import Appointments from "@/pages/Appointments";
import ConnectGmail from "@/pages/ConnectGmail";
import Settings from "@/pages/Settings";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";
import CookieBanner from "@/components/CookieBanner";

function Protected({ children, requireOnboarded = true }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="label-cap text-[var(--ink-faint)]">…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (requireOnboarded && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="label-cap text-[var(--ink-faint)]">…</p>
      </div>
    );
  }
  if (user && user.id) {
    return <Navigate to={user.onboarded ? "/app" : "/onboarding"} replace />;
  }
  return children;
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route
              path="/onboarding"
              element={
                <Protected requireOnboarded={false}>
                  <Onboarding />
                </Protected>
              }
            />
            <Route
              path="/app"
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="chat" element={<Chat />} />
              <Route path="diary" element={<Diary />} />
              <Route path="ingest" element={<Ingest />} />
              <Route path="proposals" element={<Proposals />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="shopping" element={<Shopping />} />
              <Route path="recipes" element={<Recipes />} />
              <Route path="family" element={<Family />} />
              <Route path="family/agreements" element={<Agreements />} />
              <Route path="memory" element={<Memory />} />
              <Route path="medications" element={<Medications />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="connect/gmail" element={<ConnectGmail />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <CookieBanner />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
